import { Component } from 'react';

import { injectIntl, FormattedMessage, IntlShape } from 'react-intl';
import { debounce } from 'lodash';

import {
  searchAutocompleteHandler,
  drawCircle,
  drawPolygon,
  resetCircleGeofence,
  resetPolygonGeofence,
  drawPlaceholder,
  getPolygonBounds,
  getPolygonHullPoints,
  googleMapsCoordsToArray,
  arrayToGoogleMapsCoords,
} from './GeofenceMap.utils';
import { getCircleRadius } from './GeofenceMap.utils.math';

import GoogleMapReact, { Coords } from 'google-map-react';

import { Input } from 'styles/form';
import {
  Container,
  ButtonsContainer,
  EdgeMarker,
  StyledUndoButton,
  StyledResetButton,
  InnerButtonsContainer,
  StyledRedoButton,
} from './GeofenceMap.styles';
import { UserGeofenceType } from 'constants/geofence';
import UndoIcon from 'assets/UndoIcon';
import RedoIcon from 'assets/RedoIcon';
import { UserDefinedGeofence, UserDefinedGeofenceRecord } from 'services/resources';

const DEFAULT_ZOOM = 11;
const DEFAULT_CENTER = {
  lat: 45.501211,
  lng: -73.665086,
};

type Coordinate = { lat: number; lng: number };

interface GeofenceMapProps {
  ovalTool: boolean;
  polygonTool: boolean;
  editEnabled: boolean;
  editGeofence: UserDefinedGeofenceRecord;
  displayGeofence: UserDefinedGeofenceRecord;
  onSetCreatedGeofence: (geofence: UserDefinedGeofence) => void;
  resizing: boolean;
  intl: IntlShape;
  center?: Coordinate;
  zoom?: number;
}

interface GeofenceMapState {
  map: google.maps.Map | null;
  mapsApi: typeof google.maps | null;
  searchQuery: string;
  markers: Coordinate[];
  redoMarkers: Coordinate[];
  mouseMoveEvent: object;
}

class GeofenceMap extends Component<GeofenceMapProps, GeofenceMapState> {
  static defaultProps = {
    editEnabled: false,
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
  };

  state = {
    map: null,
    mapsApi: null,

    searchQuery: '',
    markers: [],
    redoMarkers: [],

    mouseMoveEvent: null,
  };

  componentDidMount() {
    window.addEventListener('resize', this.boundUpdateGeofence);
    if (this.props.editEnabled && this.props.editGeofence) {
      this.setMarkers();
    }
  }

  componentDidUpdate = (prevProps: GeofenceMapProps) => {
    const { displayGeofence } = prevProps;

    // Add or remove mousemove listener for the map
    if (this.props.ovalTool && !this.state.mouseMoveEvent && this.state.map) {
      this.setState({
        mouseMoveEvent: this.state.map.addListener('mousemove', this.handleDrawGeofencePlaceholder),
      });
    } else if (!this.props.ovalTool && this.state.mapsApi && this.state.mouseMoveEvent) {
      this.state.mapsApi.event.removeListener(this.state.mouseMoveEvent);
      this.setState({ mouseMoveEvent: null });
    }

    // If the tool is changed, we're getting rid of the markers entered by the previous marker
    if (
      prevProps.ovalTool !== this.props.ovalTool ||
      prevProps.polygonTool !== this.props.polygonTool ||
      prevProps.editEnabled !== this.props.editEnabled
    ) {
      resetCircleGeofence();
      resetPolygonGeofence();

      this.setState({ markers: [] });
    }

    // If a geofence is passed, we will apply the markers
    if (prevProps.editEnabled !== this.props.editEnabled && this.props.editGeofence) {
      this.setMarkers();
    }

    // If the geofence to display changes or if the edit/create are toggled, we need to figure out what to display
    // While the object reference might be different, the geofence might be the same so the first to checks need to be added
    if (
      (displayGeofence === null && this.props.displayGeofence !== null) ||
      (displayGeofence !== null && this.props.displayGeofence === null) ||
      (displayGeofence && this.props.displayGeofence && displayGeofence.id !== this.props.displayGeofence.id) ||
      (prevProps.editEnabled !== this.props.editEnabled && this.props.displayGeofence !== null)
    ) {
      // If edit/create is enabled or if a display geofence is not selected, there's no point to continue
      if (this.props.displayGeofence && !this.props.editEnabled && !this.props.editGeofence) {
        resetCircleGeofence();
        resetPolygonGeofence();

        // If edit/create was toggled to false, display the geofence that was selected
        this.debouncedUpdateGeofence(this.props);
      }
    }

    if (this.props.resizing) {
      this.debouncedUpdateGeofence(this.props);
    }
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.boundUpdateGeofence);
  }

  setMarkers() {
    if (this.props.editGeofence.type === UserGeofenceType.CIRCLE) {
      const points = [this.props.editGeofence.center, this.props.editGeofence.edge].map(arrayToGoogleMapsCoords);

      this.setState({ markers: points });
      this.handleGeofenceChanged(UserGeofenceType.CIRCLE, points);
    } else if (this.props.editGeofence.type === UserGeofenceType.POLYGON) {
      const points = this.props.editGeofence.polygon.map(arrayToGoogleMapsCoords);

      this.setState({ markers: points });
      this.handleGeofenceChanged(UserGeofenceType.POLYGON, points);
    }
  }

  debouncedUpdateGeofence = debounce((props: GeofenceMapProps) => {
    this.updateGeofence(props);
  }, 200);

  boundUpdateGeofence = () => {
    this.debouncedUpdateGeofence(this.props);
  };

  updateGeofence = (props: GeofenceMapProps) => {
    if (props.displayGeofence && props.displayGeofence.type === UserGeofenceType.CIRCLE) {
      // @ts-expect-error [number, number] improperly infered as number[]
      this.drawCircleAndFitMap(arrayToGoogleMapsCoords(props.displayGeofence.center), props.displayGeofence.radius);
    } else if (props.displayGeofence && props.displayGeofence.type === UserGeofenceType.POLYGON) {
      this.drawPolygonAndFitMap(props.displayGeofence.polygon.map(arrayToGoogleMapsCoords));
    }
  };

  drawCircleAndFitMap = (center: Coords, radius: number) => {
    const fence = drawCircle(this.state.map, this.state.mapsApi, center, null, radius);

    if (fence) {
      this.fitMapToBounds(fence.getBounds());
    }
  };

  drawPolygonAndFitMap = (points: Coords[]) => {
    const { mapsApi } = this.state;
    const fence = drawPolygon(this.state.map, mapsApi, points);

    if (fence && mapsApi) {
      const emptyBounds = new mapsApi.LatLngBounds();
      const bounds = getPolygonBounds(emptyBounds, fence);
      this.fitMapToBounds(bounds);
    }
  };

  fitMapToBounds = (bounds) => {
    const { map } = this.state;

    if (map) {
      map.fitBounds(bounds);
    }
  };

  handleOnClick = (event) => {
    if (!this.props.editEnabled) {
      return;
    }

    if (this.props.ovalTool) {
      this.handleOvalToolOnClick(event);
    } else {
      this.handlePolygonToolOnClick(event);
    }
  };

  handleClear = () => {
    this.setState({ markers: [], redoMarkers: [] });

    this.handleResetGeofeneceDrawing();
  };

  handleUndo = () => {
    this.setState(
      (prevState) => {
        if (prevState.markers.length === 0) return prevState;
        const undoneMarker = prevState.markers[prevState.markers.length - 1];
        return {
          ...prevState,
          markers: prevState.markers.slice(0, prevState.markers.length - 1),
          redoMarkers: [...prevState.redoMarkers, undoneMarker],
        };
      },
      () => {
        if (this.props.ovalTool) {
          this.handleGeofenceChanged(UserGeofenceType.CIRCLE, this.state.markers);
        } else if (this.props.polygonTool) {
          let hullPoints = [];

          if (this.state.markers.length > 0) {
            hullPoints = getPolygonHullPoints(this.state.mapsApi, this.state.markers);
          }

          this.handleGeofenceChanged(UserGeofenceType.POLYGON, hullPoints);
        }
      }
    );
  };

  handleRedo = () => {
    this.setState(
      (prevState) => {
        if (prevState.redoMarkers.length === 0) return prevState;
        const markerToRedo = prevState.redoMarkers[prevState.redoMarkers.length - 1];
        return {
          ...prevState,
          markers: [...prevState.markers, markerToRedo],
          redoMarkers: prevState.redoMarkers.slice(0, prevState.redoMarkers.length - 1),
        };
      },
      () => {
        if (this.props.ovalTool) {
          this.handleGeofenceChanged(UserGeofenceType.CIRCLE, this.state.markers);
        } else if (this.props.polygonTool) {
          let hullPoints = [];

          if (this.state.markers.length > 0) {
            hullPoints = getPolygonHullPoints(this.state.mapsApi, this.state.markers);
          }

          this.handleGeofenceChanged(UserGeofenceType.POLYGON, hullPoints);
        }
      }
    );
  };

  handleResetGeofeneceDrawing = () => {
    resetCircleGeofence();
    resetPolygonGeofence();

    this.props.onSetCreatedGeofence(null);
  };

  handleChangeSearchQuery = ({ currentTarget: { value } }) => {
    this.setState({ searchQuery: value });
  };

  handleDrawGeofencePlaceholder = (event) => {
    drawPlaceholder(this.state.map, this.state.mapsApi, this.state.markers, this.props.ovalTool, event);
  };

  /* eslint-disable react/no-array-index-key */
  getGeofenceMarkers = () => {
    let points = this.state.markers;

    if (this.props.polygonTool && this.state.markers.length > 0) {
      points = getPolygonHullPoints(this.state.mapsApi, this.state.markers);
    }

    return points.map((point, key) => {
      if (point && point.lat && point.lng) {
        // @ts-expect-error TS(2769): No overload matches this call.
        return <EdgeMarker key={`${key}-${point.lat}-${point.lng}`} lat={point.lat} lng={point.lng} />;
      } else {
        return null;
      }
    });
  };

  /**
   * If a geofence has changed, this function can be called to re-draw the geofence
   * and notify the parent of the change.
   *
   * If it's a circle, the points has to be a length of 2. The first point should ALWAYS
   * be the center while the second point is the edge
   */
  handleGeofenceChanged = (type: UserGeofenceType, points: Coords[]) => {
    if (type === UserGeofenceType.CIRCLE && points.length === 2) {
      const radius = getCircleRadius(points[0], points[1]);
      // The parent component will need to have a reference of this
      this.props.onSetCreatedGeofence({
        type: UserGeofenceType.CIRCLE,
        center: googleMapsCoordsToArray(points[0]),
        radius: radius,
        edge: googleMapsCoordsToArray(points[1]),
      });

      drawCircle(this.state.map, this.state.mapsApi, points[0], points[1], radius);
    } else if (type === UserGeofenceType.CIRCLE) {
      this.handleResetGeofeneceDrawing();
    }

    if (type === UserGeofenceType.POLYGON && points.length >= 3) {
      // The parent component will need to have a reference of this
      this.props.onSetCreatedGeofence({
        type: UserGeofenceType.POLYGON,
        polygon: points.map((point) => googleMapsCoordsToArray(point)),
      });

      drawPolygon(this.state.map, this.state.mapsApi, points);
    } else if (type === UserGeofenceType.POLYGON) {
      this.handleResetGeofeneceDrawing();
    }
  };

  ///////////////////////////////////////////
  // CIRCLE SPECIFIC
  ///////////////////////////////////////////

  /**
   * Handles the map click event when oval tool is selected
   */
  handleOvalToolOnClick = (event) => {
    // Clear redo history on new marker addition
    this.setState({ redoMarkers: [] });

    // If it's an oval, the user can only add 2 points (center and edge)
    if (this.props.ovalTool && this.state.markers.length === 2) {
      return;
    }

    if (this.props.ovalTool && this.state.markers.length === 1) {
      const edge = {
        lat: event.lat,
        lng: event.lng,
      };

      this.handleGeofenceChanged(UserGeofenceType.CIRCLE, [...this.state.markers, edge]);
    }

    this.setState({
      markers: [
        ...this.state.markers,
        {
          lat: event.lat,
          lng: event.lng,
        },
      ],
    });
  };

  ///////////////////////////////////////////
  // POLYGON SPECIFIC
  ///////////////////////////////////////////

  /**
   * Handles the map click event when polygon tool is selected
   */
  handlePolygonToolOnClick = (event) => {
    // Clear redo history on new marker addition
    this.setState({ redoMarkers: [] });

    // We'll keep the markers as the regular points since in the event of an Undo, we
    // want to make sure we base it on that list
    const points = [
      ...this.state.markers,
      {
        lat: event.lat,
        lng: event.lng,
      },
    ];

    // For polygons, you need at least 2 points already on the map to make an area
    if (this.props.polygonTool && this.state.markers.length >= 2) {
      // The hull points is what's required to actually draw the geofence
      const hullPoints = getPolygonHullPoints(this.state.mapsApi, points);

      this.handleGeofenceChanged(UserGeofenceType.POLYGON, hullPoints);
    }

    this.setState({ markers: points });
  };

  ///////////////////////////////////////////
  // Google API binding on load
  ///////////////////////////////////////////

  handleOnApiLoaded = ({ map, maps }) => {
    searchAutocompleteHandler(map, maps, document.getElementById('search-input'));

    this.setState({
      map,
      mapsApi: maps,
      mouseMoveEvent: map.addListener('mousemove', this.handleDrawGeofencePlaceholder),
    });

    // Once the api is loaded, if a geofence is already selected, we need to draw it
    if (this.props.displayGeofence && this.props.displayGeofence.type === UserGeofenceType.CIRCLE) {
      this.drawCircleAndFitMap(
        // @ts-expect-error [number, number] improperly infered as number[]
        arrayToGoogleMapsCoords(this.props.displayGeofence.center),
        this.props.displayGeofence.radius
      );
    }

    if (this.props.displayGeofence && this.props.displayGeofence.type === UserGeofenceType.POLYGON) {
      this.drawPolygonAndFitMap(this.props.displayGeofence.polygon.map(arrayToGoogleMapsCoords));
    }
  };

  createMapOptions = (maps) => ({
    draggableCursor: 'crosshair',
    fullscreenControl: false,
    cameraControl: true,
    mapTypeControl: false,
    zoomControl: false,
    mapTypeControlOptions: {
      position: maps.ControlPosition.LEFT_BOTTOM,
    },
    zoomControlOptions: {
      position: maps.ControlPosition.LEFT_BOTTOM,
    },
    rotateControl: false,
    tilt: 0,
  });

  render() {
    if (this.props.resizing) {
      return null;
    }

    return (
      <Container>
        <Input
          id="search-input"
          type="name"
          name="search"
          placeholder={this.props.intl.formatMessage({
            id: 'components.GeofenceMap.search',
          })}
        />
        <GoogleMapReact
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={DEFAULT_ZOOM}
          onClick={this.handleOnClick}
          onGoogleApiLoaded={this.handleOnApiLoaded}
          options={this.createMapOptions}
          resetBoundsOnResize
          yesIWantToUseGoogleMapApiInternals
        >
          {this.getGeofenceMarkers()}
        </GoogleMapReact>

        <ButtonsContainer visible={this.props.editEnabled}>
          <InnerButtonsContainer>
            <StyledUndoButton
              icon={<UndoIcon />}
              onClick={this.handleUndo}
              disabled={this.state.markers.length === 0}
            />
            <StyledRedoButton
              icon={<RedoIcon />}
              onClick={this.handleRedo}
              disabled={this.state.markers.length === 0}
            />
            <StyledResetButton onClick={this.handleClear} disabled={this.state.markers.length === 0}>
              <FormattedMessage id="components.GeofenceMap.reset" />
            </StyledResetButton>
          </InnerButtonsContainer>
        </ButtonsContainer>
      </Container>
    );
  }
}

export default injectIntl(GeofenceMap);
